<script lang="ts" setup>
import {ref, onMounted, onBeforeUnmount, onActivated, onDeactivated, computed, onBeforeMount, nextTick} from 'vue';
import {LibraryContentTypeDTO, type ExploreLibraryItemDTO} from '@/ts/types/dto/recco/library.dto';
import {EXPLORE_LIBRARY_PAGE_SIZE} from '@/ts/api/modules/content-api';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {usePagedCollection} from '@/ts/composables/stateful/use-paged-collection';
import LibraryCollection from '@/vue/organisms/recco/library-collection.vue';
import {useRouter} from 'vue-router';
import {useI18n} from 'vue-i18n';
import {pagesPath} from '@/ts/router/pages-path';
import {useFeatureAccess} from '@/ts/composables/stateful/use-feature-access';
import {wasPopNavigation} from '@/ts/utils/history-navigation';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';

defineOptions({name: 'PartnerContentPage'});

const logger = useLogger('PartnerContentPage');
const api = useApi();
const router = useRouter();
const {t: $t} = useI18n();
const {canAccessExplore} = useFeatureAccess();

// Read by pack alone, exactly as the band on Explore is, so the list opened from "View all" is the
// same set the band was drawn from. Programs are excluded like everywhere else in Explore.
const PARTNER_FACETS = {
  mediaTypes: [],
  topics: [],
  saved: false,
  recommended: false,
  contentTypes: [LibraryContentTypeDTO.Challenge, LibraryContentTypeDTO.Educational],
};
// Same window explore-page and the topic page use for their own silent refresh.
const REFRESH_STALE_AFTER_MS = 5 * 60 * 1000;
let lastLoadedAt = 0;

/** Pack ids, fetched before the first page — the facet the whole list is keyed on. */
const packIds = ref<UuidDTO[]>([]);

const collectionState = usePagedCollection<ExploreLibraryItemDTO>({
  key: (item) => `${item.contentType}-${item.content?.id ?? item.challenge?.id}`,
  rootSelector: '.global-page-template .content',
  onMissingRoot: () => {
    logger.error('Partner content found no .content scroller; paging falls back to the viewport root')();
  },
  fetchPage: async(page) => {
    const result = await api.content.getExploreLibrary(
      {...PARTNER_FACETS, partnerIds: packIds.value},
      EXPLORE_LIBRARY_PAGE_SIZE,
      page,
    );
    return {items: result.items, page: result.page, totalElements: result.totalElements};
  },
  onError: (err, first) => {
    logger.error(first ? 'Failed to fetch the partner content' : 'Failed to load more partner content', {err})();
  },
});

const {items, totalElements, loadedCount, loading, loadingMore, error, moreError, stoppedShort,
  collection, setSentinel, setFirstPageLoader, mountObserver, syncObserver, pauseObserver, disconnectObserver,
  loadFirstPage, refreshInPlace, retryFirstPage, retryMorePages} = collectionState;

/**
 * Packs first, then the list: it cannot be asked for without the facet.
 *
 * A failed partners call is an error, not an empty pack. Conflating them renders "Nothing here yet"
 * with no retry — indistinguishable from an org that genuinely has none, and with no way out, since
 * this is the whole page rather than one section of it.
 */
const loadPartnerContent = async(): Promise<void> => {
  // Reset here rather than relying on loadFirst: on a retry the partners call runs first, and
  // without this the error card stays up while it is in flight.
  loading.value = true;
  error.value = false;
  try {
    packIds.value = (await api.content.getExplorePartners()).map((partner) => partner.id);
  } catch (err) {
    logger.error('Failed to fetch explore partners', {err})();
    packIds.value = [];
    error.value = true;
    loading.value = false;
    return;
  }
  if (packIds.value.length === 0) {
    // A genuinely pack-less org: the empty state, not an error.
    loading.value = false;
    lastLoadedAt = Date.now();
    return;
  }
  await loadFirstPage();
  lastLoadedAt = Date.now();
};

// Retry re-runs the pack fetch: without it a failed partners call retries the list with an empty
// facet, which the API reads as "no filter" and answers with the whole catalogue — presented to
// the reader as their organisation's content.
setFirstPageLoader(loadPartnerContent);

const countLabel = computed<string>(() =>
  $t('explore_n_items', totalElements.value, {named: {count: totalElements.value}}));
const loadedStatusLabel = computed<string>(() =>
  $t('explore_topic_loaded_status', totalElements.value, {
    named: {loaded: loadedCount.value, count: totalElements.value},
  }));

onBeforeMount(() => {
  if (!canAccessExplore.value) {
    void router.replace(pagesPath.intervention.interventionsList);
  }
});

let unmounted = false;

onMounted(async() => {
  await loadPartnerContent();
  if (unmounted) {
    return;
  }
  mountObserver();
  await nextTick();
  syncObserver();
});

onBeforeUnmount(() => {
  unmounted = true;
  disconnectObserver();
});

onDeactivated(() => {
  // The sentinel is detached while the page is off screen; stop watching rather than leave a live
  // observer on a node nobody can see.
  pauseObserver();
});

onActivated(() => {
  // Re-checked on every visit: a licence can drop the feature mid-session, and a cached instance
  // never re-runs the onBeforeMount guard.
  if (!canAccessExplore.value) {
    void router.replace(pagesPath.intervention.interventionsList);
    return;
  }
  // Keeping the instance alive freezes its rows, so consumed markers and challenge progress would
  // stay as they were for the session on a page that is entirely partner content. A push entry
  // always refreshes; the window applies only to a back/forward, where the reader is returning to
  // what they were just looking at.
  const stale = !wasPopNavigation() || Date.now() - lastLoadedAt > REFRESH_STALE_AFTER_MS;
  if (packIds.value.length === 0) {
    // Nothing held, so there is nothing on screen to disturb. Re-run the whole load: the pack may
    // have failed to arrive, or been added since, and refreshInPlace can do neither — it declines
    // an empty list, and the pack fetch lives outside it.
    //
    // Not while one is in flight: this hook also runs on the first activation, where the mount's
    // own load has not settled yet, and starting a second would race it — the first failure would
    // be cleared by the retry before the reader ever saw it.
    if (stale && !loading.value) {
      void loadPartnerContent();
    }
    return;
  }
  if (stale) {
    void refreshInPlace().then((refreshed) => {
      if (refreshed) {
        lastLoadedAt = Date.now();
      }
    });
    return;
  }
  void nextTick(() => {
    syncObserver();
  });
});
</script>

<template>
  <library-collection
    ref="collection"
    :title="$t('explore_featured_by_org')"
    :count-label="countLabel"
    :status-label="loadedStatusLabel"
    back-label="explore_featured_by_org"
    :items="items"
    :loading="loading"
    :error="error"
    :loading-more="loadingMore"
    :show-retry-more="moreError || stoppedShort"
    :set-sentinel="setSentinel"
    @retry-first="retryFirstPage"
    @retry-more="retryMorePages"
  />
</template>
